import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import { CancelToken, isCancel } from 'axios';
import { API, Auth } from 'aws-amplify';
import AppContext from '../../AppContext';
import Grid from '../../components/Grid/Grid'
import UserForm from '../../forms/UserForm';
import UserUpdateForm from '../../forms/UserUpdateForm';
import ButtonGroup from '../../components/Grid/CustomComponents/ButtonGroup';
import { UserIcon, TrashIcon, LockOpenIcon } from '@heroicons/react/24/solid';

const UserManagementView = (props) => {

  const { primaryColor, secondaryColor, setModal, setConfirmationContent } = useContext(AppContext)
  const [data, setData] = useState([]);
  const [cancel] = useState(CancelToken.source());
  const [loading, setLoading] = useState(true);
  const [failureMessage, setFailureMessage] = useState();

  const getData = async () => {
      try {
          setLoading(true);
          const myInit = {
            response: true,
            headers: { Authorization: (await Auth.currentSession()).getIdToken().getJwtToken() }
          };

          const response = await API.post('APIEndpoint', `/get-users`, myInit) 
          setData(response?.data?.results || []);
          setLoading(false);
      } catch (error) {
          if (isCancel(error)) {
            console.log('Request canceled', error.message);
          } else {
            console.log(error);
            setLoading(false);
            setFailureMessage("The app encountered an error when retrieving this data. Please contact your administrator.");
          }
      }
  };

  useEffect(() => {
      getData();

      return () => cancel.cancel();
      // eslint-disable-next-line
  },[props?.clientIdOverride, cancel]);

  const addUser = () => {
      setModal(<UserForm data={data} refreshData={getData} primaryColor={primaryColor} secondaryColor={secondaryColor} />);
  }

  const editUser = useCallback((props) => {
      setModal(<UserUpdateForm data={props} refreshData={getData} />);
  }, [setModal])

  const resetPasswordApiCall = useCallback(async (props) => {
      try {
        const myInit = {
          response: true,
          headers: { Authorization: (await Auth.currentSession()).getIdToken().getJwtToken() },
          body: {
            "userEmail": props?.email
          }
        };

        await API.post('APIEndpoint', `/reset-password`, myInit) 
          getData();
      } catch(error) {
          console.log('Failed to reset Password')
      }
  }, [])

  const resetPassword = useCallback((props) => {
      setConfirmationContent(`Are you sure you want to reset the password for ${props?.email}?`, () => resetPasswordApiCall(props), 'Reset Password');
  }, [resetPasswordApiCall, setConfirmationContent])


  const deleteApiCall = useCallback(async (props) => {
      try {
          const myInit = {
            response: true,
            headers: { Authorization: (await Auth.currentSession()).getIdToken().getJwtToken() },
            body: { "userEmail": props?.email }
          };

          await API.post('APIEndpoint', `/delete-user`, myInit) 
          getData();
      } catch(error) {
          console.log('Failed to delete user')
      }
  }, [])

  const deleteUser = useCallback((props) => {
    setConfirmationContent(`Are you sure you want to DELETE user ${props?.email}?`, () => deleteApiCall(props), 'DELETE User');
  }, [setConfirmationContent, deleteApiCall])

  const columnButtons = useMemo(() => { return [{
      headerName: 'Manage User',
      cellClass: 'overflow-cell',
      cellRenderer: (props) => (
          <ButtonGroup 
              {...props}
              buttons={[
                  {icon: UserIcon, 'onClick': editUser, name: 'Edit User'},
                  {icon: LockOpenIcon, 'onClick': resetPassword, name: 'Reset Password', disabled: !props.data?.enabled},
                  {icon: TrashIcon, 'onClick': deleteUser, name: 'DELETE User'},
              ]}
          />
      ),
      sortable: false,
      // filterable: false,
      minWidth: 150,
      width: 150,
      suppressMenu:true,
      enablePivot: false, //come back and fix this!
      enableRowGroup: false, //come back and fix this!
      enableValue: false, //come back and fix this! 
      pinned: 'right'
    }]
  },[editUser, resetPassword, deleteUser])

  return (
      <Grid
          loading={loading}
          failureMessage={failureMessage}
          data={data}
          buttons={[{label:'Add User', onClick: addUser, disabled: false}]}
          columnButtons={columnButtons}
          exportName={`Users`}
          app_usage={'app_user'}
          maxColumnWidth={250}
      />
  );
};


export default UserManagementView;